import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { formatReportColumnLabel } from "@/utility/fclReportHelpers";
import {
    buildExcelColumnDefs,
    filterRowsForExcelExport,
    isExcludedExportKey,
    mapRowsForExcelExport,
    toExcelCellValue,
} from "./reportExportData";

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2563EB" } };

/**
 * Export report data to .xlsx (columns match report table when `columns` provided).
 * @param {{ fileName: string, sheets: { name: string, data: object[], columns?: object[] }[] }} options
 */
export async function exportReportToExcel({ fileName, sheets = [] }) {
    const workbook = new ExcelJS.Workbook();
    let added = 0;

    sheets.forEach((sheet, sheetIndex) => {
        const rawRows = sheet?.data;
        if (!Array.isArray(rawRows) || rawRows.length === 0) return;

        const tableColumns = sheet?.columns;
        let exportRows = tableColumns?.length
            ? mapRowsForExcelExport(rawRows, tableColumns)
            : filterRowsForExcelExport(rawRows);

        if (!exportRows.length) return;

        let columnDefs = tableColumns?.length
            ? buildExcelColumnDefs(tableColumns)
            : Object.keys(exportRows[0] || {})
                  .filter((key) => !isExcludedExportKey(key))
                  .map((key) => ({
                      key,
                      header: formatReportColumnLabel(key),
                  }));

        if (!columnDefs.length && exportRows.length) {
            exportRows = filterRowsForExcelExport(rawRows);
            columnDefs = Object.keys(exportRows[0] || {})
                .filter((key) => !isExcludedExportKey(key))
                .map((key) => ({
                    key,
                    header: formatReportColumnLabel(key),
                }));
        }

        if (!columnDefs.length) return;

        const ws = workbook.addWorksheet(
            String(sheet.name || `Sheet${sheetIndex + 1}`).slice(0, 31)
        );

        ws.columns = columnDefs.map((col) => ({
            header: col.header,
            key: col.key,
            width: 16,
        }));

        const headerRow = ws.getRow(1);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = HEADER_FILL;
            cell.alignment = { vertical: "middle", horizontal: "center" };
        });
        headerRow.height = 24;

        exportRows.forEach((row, index) => {
            const line = {};
            columnDefs.forEach(({ key }) => {
                line[key] = toExcelCellValue(row[key]);
            });
            const dataRow = ws.addRow(line);
            if (index % 2 === 1) {
                dataRow.eachCell((cell) => {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFF3F4F6" },
                    };
                });
            }
        });

        added += 1;
    });

    if (!added) {
        throw new Error("NO_DATA");
    }

    const safeName = String(fileName || "Report").replace(/[^\w\-]+/g, "_");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${safeName}_${new Date().toISOString().split("T")[0]}.xlsx`
    );
}
